<app-report-header
  crumbLabel="Estimated Weekly Rent"
  [loadingDownload]="loadingDownloadbtn"
  (download)="convetToPDF()"
  (recalculate)="startagain()">
</app-report-header>

<div class="rpt-page" id="contentToConvert">

  <!-- ── Masthead ── -->
  <div class="rpt-wrap rpt-masthead">
    <div class="rpt-masthead__eyebrow">Estimated Weekly Rent Report</div>
    <h1 class="rpt-masthead__title">Rental Income — £{{calcData?.PropertyValue | number:'1.0-0'}} Property</h1>
    <p class="rpt-masthead__subtitle">
      Projected annual, monthly, and weekly rental income based on your property value and rental yield assumptions.
    </p>
    <div class="rpt-masthead__meta">
      <span class="rpt-meta-pill">Property Value <strong>£{{calcData?.PropertyValue | number:'1.0-0'}}</strong></span>
      <span class="rpt-meta-pill">Gross Yield <strong>{{calcData?.rentalYeild | number:'1.2-2'}}%</strong></span>
      <span class="rpt-meta-pill" *ngIf="homeCurrencyCode">Home Currency <strong>{{homeCurrencyCode}}</strong></span>
      <span class="rpt-meta-pill rpt-meta-pill--accent">Annual Rent <strong>£{{annualGBP | number:'1.0-0'}}</strong></span>
    </div>
  </div>

  <!-- ── §01 KPI Section ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">01 · Results</div>
        <h2 class="rpt-section__title">Rental income breakdown</h2>
        <p class="rpt-section__sub">Annual, monthly, and weekly rental income in GBP and your home currency.</p>
      </div>
    </div>

    <div class="rpt-kpi-strip">
      <!-- Hero: Annual Rent -->
      <div class="rpt-kpi-hero">
        <div class="rpt-kpi-hero__eyebrow">Annual Rental Income</div>
        <div class="rpt-kpi-hero__body">
          <div class="rpt-kpi-hero__value">£{{annualGBP | number:'1.0-0'}}</div>
          <div class="rpt-kpi-hero__label" *ngIf="homeCurrencyCode==='INR'">₹{{annualHomeFmt}} equivalent</div>
          <div class="rpt-kpi-hero__label" *ngIf="homeCurrencyCode && homeCurrencyCode!=='INR'">{{homeCurrencyCode}} {{annualHome | number:'1.0-0'}} equivalent</div>
        </div>
        <p class="rpt-kpi-hero__caption">
          Based on £{{calcData?.PropertyValue | number:'1.0-0'}} property value at {{calcData?.rentalYeild | number:'1.2-2'}}% gross yield.
        </p>
      </div>

      <!-- Side: Weekly + Monthly -->
      <div class="rpt-kpi-side rpt-kpi-side--2">
        <div class="rpt-kpi-card">
          <div class="rpt-kpi-card__label">Weekly Rent</div>
          <div class="rpt-kpi-card__value">£{{weeklyGBP | number:'1.0-0'}}</div>
          <div class="rpt-kpi-card__foot" *ngIf="homeCurrencyCode==='INR'">₹{{weeklyHomeFmt}} equivalent</div>
          <div class="rpt-kpi-card__foot" *ngIf="homeCurrencyCode && homeCurrencyCode!=='INR'">{{homeCurrencyCode}} {{weeklyHome | number:'1.0-0'}} equivalent</div>
        </div>
        <div class="rpt-kpi-card">
          <div class="rpt-kpi-card__label">Monthly Rent</div>
          <div class="rpt-kpi-card__value">£{{monthlyGBP | number:'1.0-0'}}</div>
          <div class="rpt-kpi-card__foot" *ngIf="homeCurrencyCode==='INR'">₹{{monthlyHomeFmt}} equivalent</div>
          <div class="rpt-kpi-card__foot" *ngIf="homeCurrencyCode && homeCurrencyCode!=='INR'">{{homeCurrencyCode}} {{monthlyHome | number:'1.0-0'}} equivalent</div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §02 Assumptions ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">02 · Inputs</div>
        <h2 class="rpt-section__title">Your assumptions</h2>
      </div>
    </div>
    <div class="rpt-card">
      <div class="rpt-card__body">
        <div class="rpt-arow">
          <span class="rpt-arow__key">Property Value</span>
          <span class="rpt-arow__val">£{{calcData?.PropertyValue | number:'1.0-0'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Rental Yield</span>
          <span class="rpt-arow__val">{{calcData?.rentalYeild | number:'1.2-2'}}%</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Home Currency</span>
          <span class="rpt-arow__val">{{calcData?.homecurrencyText || '—'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">FX Rate (1 GBP)</span>
          <span class="rpt-arow__val">{{calcData?.homecurrency || '—'}} {{calcData?.homecurrencyText}}</span>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §03 Breakdown ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">03 · Breakdown</div>
        <h2 class="rpt-section__title">Full rental breakdown</h2>
        <p class="rpt-section__sub">Rental income in GBP and your home currency.</p>
      </div>
    </div>

    <div class="rpt-card">
      <div class="rpt-breakdown">
        <!-- GBP column -->
        <div class="rpt-breakdown__col">
          <div class="rpt-breakdown__header">GBP</div>
          <div class="rpt-arow">
            <span class="rpt-arow__key">Annual Rent</span>
            <span class="rpt-arow__val">£{{annualGBP | number:'1.0-0'}}</span>
          </div>
          <div class="rpt-arow">
            <span class="rpt-arow__key">Monthly Rent</span>
            <span class="rpt-arow__val">£{{monthlyGBP | number:'1.0-0'}}</span>
          </div>
          <div class="rpt-arow">
            <span class="rpt-arow__key">Weekly Rent</span>
            <span class="rpt-arow__val">£{{weeklyGBP | number:'1.0-0'}}</span>
          </div>
          <div class="rpt-arow">
            <span class="rpt-arow__key">Gross Yield</span>
            <span class="rpt-arow__val rpt-arow__val--accent">{{grossYield | number:'1.2-2'}}%</span>
          </div>
        </div>

        <!-- Home Currency column -->
        <div class="rpt-breakdown__col" *ngIf="homeCurrencyCode">
          <div class="rpt-breakdown__header">{{homeCurrencyCode}}</div>
          <div class="rpt-arow">
            <span class="rpt-arow__key">Annual Rent</span>
            <span class="rpt-arow__val">
              <span *ngIf="homeCurrencyCode==='INR'">₹{{annualHomeFmt}}</span>
              <span *ngIf="homeCurrencyCode!=='INR'">{{annualHome | number:'1.0-0'}}</span>
            </span>
          </div>
          <div class="rpt-arow">
            <span class="rpt-arow__key">Monthly Rent</span>
            <span class="rpt-arow__val">
              <span *ngIf="homeCurrencyCode==='INR'">₹{{monthlyHomeFmt}}</span>
              <span *ngIf="homeCurrencyCode!=='INR'">{{monthlyHome | number:'1.0-0'}}</span>
            </span>
          </div>
          <div class="rpt-arow">
            <span class="rpt-arow__key">Weekly Rent</span>
            <span class="rpt-arow__val">
              <span *ngIf="homeCurrencyCode==='INR'">₹{{weeklyHomeFmt}}</span>
              <span *ngIf="homeCurrencyCode!=='INR'">{{weeklyHome | number:'1.0-0'}}</span>
            </span>
          </div>
          <div class="rpt-arow">
            <span class="rpt-arow__key">FX Rate</span>
            <span class="rpt-arow__val">1 GBP = {{calcData?.homecurrency}} {{homeCurrencyCode}}</span>
          </div>
        </div>
      </div>
    </div>
  </section>

</div>

<app-report-footer
  [today]="today"
  [loadingDownload]="loadingDownloadbtn"
  [loadingShare]="loadingSharebtn"
  (download)="convetToPDF()"
  (recalculate)="startagain()"
  (share)="shareReport($event)">
</app-report-footer>
